"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ISpiceSourceOptsSchema = exports.SpiceSourceTransientNoiseOptsSchema = exports.SpiceSourceSinusoidalDefinition = exports.SpiceSourceSinusoidalOptsSchema = exports.SpiceSourcePulseDefinition = exports.SpiceSourcePulseOptsSchema = exports.SpiceSourceExponentialDefinition = exports.SpiceSourceExponentialOptsSchema = exports.SpiceSourceDCDefinition = exports.SpiceSourceDCOptsSchema = exports.ISpiceSourceDefinitionSchema = void 0;
const zod_1 = require("zod");
const ComponentModelParameterDefinition_1 = require("../ComponentModelParameterDefinition");
exports.ISpiceSourceDefinitionSchema = zod_1.z.object({
    type: zod_1.z.string(),
    sourceUnitParameters: zod_1.z.array(zod_1.z.string()),
    parameters: ComponentModelParameterDefinition_1.ComponentModelParameterDefinitionMapSchema,
});
exports.SpiceSourceDCOptsSchema = zod_1.z.object({
    sourceType: zod_1.z.literal('DC'),
    dcValue: zod_1.z.number(),
});
exports.SpiceSourceDCDefinition = {
    type: 'DC',
    sourceUnitParameters: ['dcValue'],
    parameters: {
        dcValue: {
            name: 'dcValue',
            type: 'number',
            required: true,
        },
    },
};
exports.SpiceSourceExponentialOptsSchema = zod_1.z.object({
    sourceType: zod_1.z.literal('EXP'),
    V1: zod_1.z.number(),
    V2: zod_1.z.number(),
    TD1: zod_1.z.number().optional(),
    TAU1: zod_1.z.number().optional(),
    TD2: zod_1.z.number().optional(),
    TAU2: zod_1.z.number().optional(),
    TSTEP: zod_1.z.number().optional(),
});
exports.SpiceSourceExponentialDefinition = {
    type: 'EXP',
    sourceUnitParameters: ['V1', 'V2'],
    parameters: {
        V1: {
            name: 'V1',
            type: 'number',
            required: true,
            description: 'Voltage or current value at time t=0, representing the initial value of the waveform before any exponential change occurs.',
        },
        V2: {
            name: 'V2',
            type: 'number',
            required: true,
            description: 'Voltage or current value during pulse, representing the value of the waveform after the exponential rise or fall.',
        },
        TD1: {
            name: 'TD1',
            type: 'number',
            required: false,
            description: 'Delay time before rising edge of pulse, the time interval before the exponential rise begins.',
        },
        TAU1: {
            name: 'TAU1',
            type: 'number',
            required: false,
            description: 'Rise time constant, the time constant for the exponential rise, determining how quickly the waveform rises to its peak value.',
        },
        TD2: {
            name: 'TD2',
            type: 'number',
            required: false,
            description: 'Delay time before falling edge of pulse, the time interval before the exponential fall begins.',
        },
        TAU2: {
            name: 'TAU2',
            type: 'number',
            required: false,
            description: 'Fall time constant, the time constant for the exponential fall, determining how quickly the waveform falls back to its initial value.',
        },
        TSTEP: {
            name: 'TSTEP',
            type: 'number',
            required: false,
            description: 'Time step, the interval between each step in the simulation of the exponential waveform.',
        },
    },
};
exports.SpiceSourcePulseOptsSchema = zod_1.z.object({
    sourceType: zod_1.z.literal('PULSE'),
    InitialValue: zod_1.z.number(),
    PulsedValue: zod_1.z.number(),
    DelayTime: zod_1.z.number(),
    RiseTime: zod_1.z.number().optional(),
    FallTime: zod_1.z.number().optional(),
    PulseWidth: zod_1.z.number(),
    Period: zod_1.z.number().optional(),
    Phase: zod_1.z.number().optional(),
});
exports.SpiceSourcePulseDefinition = {
    type: 'PULSE',
    sourceUnitParameters: ['InitialValue', 'PulsedValue'],
    parameters: {
        InitialValue: {
            name: 'InitialValue',
            type: 'number',
            required: true,
            description: 'Initial value of the pulse, the starting voltage or current before the pulse begins.',
        },
        PulsedValue: {
            name: 'PulsedValue',
            type: 'number',
            required: true,
            description: 'Pulsed value of the pulse, the voltage or current value during the pulse.',
        },
        DelayTime: {
            name: 'DelayTime',
            type: 'number',
            required: true,
            description: 'Delay time before the pulse starts, the time interval before the pulse waveform begins.',
        },
        RiseTime: {
            name: 'RiseTime',
            type: 'number',
            required: false,
            description: 'Rise time of the pulse, the time taken for the pulse to rise from the initial value to the pulsed value.',
        },
        FallTime: {
            name: 'FallTime',
            type: 'number',
            required: false,
            description: 'Fall time of the pulse, the time taken for the pulse to fall from the pulsed value back to the initial value.',
        },
        PulseWidth: {
            name: 'PulseWidth',
            type: 'number',
            required: true,
            description: 'Width of the pulse, the duration for which the pulse remains at the pulsed value.',
        },
        Period: {
            name: 'Period',
            type: 'number',
            required: false,
            description: 'Period of the pulse, the total time for one complete cycle of the pulse waveform.',
        },
        Phase: {
            name: 'Phase',
            type: 'number',
            required: false,
            description: 'Phase of the pulse, the initial phase angle of the pulse waveform.',
        },
    },
};
exports.SpiceSourceSinusoidalOptsSchema = zod_1.z.object({
    sourceType: zod_1.z.literal('SIN'),
    VO: zod_1.z.number(),
    VA: zod_1.z.number(),
    FREQ: zod_1.z.number(),
    THETA: zod_1.z.number().optional(),
    TD: zod_1.z.number().optional(),
    DAMP: zod_1.z.number().optional(),
    PHASE: zod_1.z.number().optional(),
});
exports.SpiceSourceSinusoidalDefinition = {
    type: 'SIN',
    sourceUnitParameters: ['VO', 'VA'],
    parameters: {
        VO: {
            name: 'VO',
            type: 'number',
            required: true,
            description: 'DC offset, the constant voltage or current added to the sinusoidal waveform.',
        },
        VA: {
            name: 'VA',
            type: 'number',
            required: true,
            description: 'Amplitude, the peak value of the sinusoidal waveform.',
        },
        FREQ: {
            name: 'FREQ',
            type: 'number',
            required: true,
            description: 'Frequency, the number of cycles per second of the sinusoidal waveform.',
        },
        THETA: {
            name: 'THETA',
            type: 'number',
            required: false,
            description: 'Phase shift, the initial angle of the sinusoidal waveform in degrees.',
        },
        TD: {
            name: 'TD',
            type: 'number',
            required: false,
            description: 'Time delay, the time before the sinusoidal waveform starts.',
        },
        DAMP: {
            name: 'DAMP',
            type: 'number',
            required: false,
            description: 'Damping factor, the rate at which the amplitude of the sinusoidal waveform decreases over time.',
        },
        PHASE: {
            name: 'PHASE',
            type: 'number',
            required: false,
            description: 'Phase, another term for phase shift, specifying the initial angle of the sinusoidal waveform in degrees.',
        },
    },
};
exports.SpiceSourceTransientNoiseOptsSchema = zod_1.z.object({
    sourceType: zod_1.z.literal('TRANNOISE'),
    NA: zod_1.z.number().optional(),
    NT: zod_1.z.number().optional(),
    NALPHA: zod_1.z.number().optional(),
    NAMP: zod_1.z.number().optional(),
    RTSAM: zod_1.z.number().optional(),
    RTSCAPT: zod_1.z.number().optional(),
    RTSEMT: zod_1.z.number().optional(),
});
exports.ISpiceSourceOptsSchema = zod_1.z.discriminatedUnion('sourceType', [
    exports.SpiceSourceDCOptsSchema,
    exports.SpiceSourceExponentialOptsSchema,
    exports.SpiceSourcePulseOptsSchema,
    exports.SpiceSourceSinusoidalOptsSchema,
    exports.SpiceSourceTransientNoiseOptsSchema,
]);
//# sourceMappingURL=SpiceSourceOpts.js.map